/**
 * Addons Service — frontend client for the credits-billed addons.
 *
 * Two addons currently in scope; each follows the same lifecycle:
 *
 *   GET    /addons/<addon>/status     → current state, rate, EIP/quantity
 *   POST   /addons/<addon>/enable     → provision + start meter (body opt)
 *   DELETE /addons/<addon>/disable    → tear down + stop meter
 *
 * /billing/info embeds the per-addon shape under `addons.dedicatedEgress`
 * so the BillingPage can render that card from a single response. The
 * per-addon endpoints below are kept for ENABLE/DISABLE actions (they
 * touch infra + ledger, so the dedicated handlers stay the source of
 * truth for mutation).
 *
 * Warm pooling is PER-WORKFLOW now (the "Warm Agent" toggle in each
 * pipeline's settings tab) — the legacy whole-workspace warm-pool addon
 * card was removed from /billing. The warm-pool methods below therefore
 * REQUIRE `{ projectId, workflowType }`; there is no account-level
 * (whole-workspace) enable/disable mode any more.
 */
import { accountApi as apiService } from './api';

const Addons = {
  // ── Dedicated Egress ──────────────────────────────────────────────
  async enableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/enable', { method: 'POST' });
  },
  async disableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/disable', { method: 'DELETE' });
  },
  async getDedicatedEgressStatus() {
    return apiService.request('/addons/dedicated-egress/status');
  },

  // ── Warm Pool (per-workflow) ──────────────────────────────────────
  /**
   * Enable the warm pool for a single workflow with `quantity` runners.
   * PER-PIPELINE only — `{ projectId, workflowType }` are REQUIRED. Used
   * by the pipeline settings tab's "Warm Agent" toggle.
   *
   * Atomically flips BOTH workflow.warmEnabled and account.warmPool —
   * that's what makes the reconciler actually provision the pool. Omitting
   * the workflow identifiers is rejected by the backend (400), so the
   * legacy whole-workspace enable can no longer be triggered.
   */
  async enableWarmPool(quantity, opts = {}) {
    const q = Number(quantity);
    if (!Number.isInteger(q) || q < 1) {
      throw new Error('quantity must be a positive integer');
    }
    if (!opts?.projectId || !opts?.workflowType) {
      throw new Error('projectId and workflowType are required (warm pooling is per-workflow)');
    }
    const body = {
      quantity: q,
      projectId: opts.projectId,
      workflowType: opts.workflowType,
    };
    return apiService.request('/addons/warm-pool/enable', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * Disable the warm pool for a single workflow. PER-PIPELINE only —
   * `{ projectId, workflowType }` are REQUIRED.
   *
   * Disables JUST that workflow's flag and resizes the account pool to the
   * SUM of remaining warm workflows' counts (or fully tears down billing
   * if no warm workflows are left).
   */
  async disableWarmPool(opts = {}) {
    if (!opts?.projectId || !opts?.workflowType) {
      throw new Error('projectId and workflowType are required (warm pooling is per-workflow)');
    }
    const qs = new URLSearchParams({
      projectId: opts.projectId,
      workflowType: opts.workflowType,
    });
    return apiService.request(`/addons/warm-pool/disable?${qs.toString()}`, {
      method: 'DELETE',
    });
  },
  /**
   * Warm-pool status. Pass { projectId, workflowType } for the
   * PER-PIPELINE status (pipeline Settings) — the backend reports this
   * pipeline's own warm state (from the workflow row). Called bare it
   * returns the account-level aggregate (still read by the read-only MCP
   * `zibby_get_warm_pool_status` tool).
   */
  async getWarmPoolStatus(opts = {}) {
    let path = '/addons/warm-pool/status';
    if (opts.projectId && opts.workflowType) {
      const qs = new URLSearchParams({
        projectId: opts.projectId,
        workflowType: opts.workflowType,
      });
      path += `?${qs.toString()}`;
    }
    return apiService.request(path);
  },
};

export default Addons;
